<script lang="ts" setup>
import { useRouter, useRoute } from 'vue-router'
import { DynamicForm } from '../../dynamic-form'
import { getForm } from '../../../core/services/forms'
import { onBeforeMount, ref } from 'vue'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { DLoading } from 'front-library'
import { useI18n } from 'vue-i18n'
import { useField, useForm } from 'vee-validate'
import { object, array, date, string, mixed } from 'yup'
import {
  getLicenseRequest,
  createLicenseRequest,
  updateLicenseRequest,
  getEndDate,
  getJourneyEmployee
} from '../../../core/services/licenses'
usePayrollChange(() =>
  Boolean(route.params.id) ? back() : window.location.reload()
)

/* router, notifications */
const notifications = useNotificationsStore()
const router = useRouter()
const route = useRoute()
const { t } = useI18n()

/* Datos componente Loading */
const loading = ref(false)
const messageLoading = ref('')

/* Datos */
const customForm = ref<any>({})
const form = ref<typeof DynamicForm | null>(null)
let model: any = {}

const onSubmit = async (model: any) => {
  delete model.showPercentage
  loading.value = true
  let saved = false
  try {
    if (!route.params.id) {
      saved = await createLicenseRequest(model)
    } else {
      saved = await updateLicenseRequest(model, `${route.params.id as string}`)
    }
    if (saved) {
      loading.value = false
      notifications.addNotification({
        title: !route.params.id
          ? t('notification.created')
          : t('notification.edited'),
        text: t('licenses.notification.action', [
          !route.params.id
            ? t('notification.wasCreated')
            : t('notification.wasEdited')
        ]),
        type: 'success',
        time: 10000
      })
      void router.push({ name: 'ListLicenseRequests' })
    }
  } catch (error: any) {
    notificationError(error, 'licenses', t, notifications, loading)
  }
}

const onUpdateField = async (field: any) => {
  const licenseType = form.value?.getModelValue('licenseTypeId')
  if (
    field.name === 'licenseTypeId' ||
    field.name === 'days' ||
    field.name === 'startDate'
  ) {
    const days = form.value?.getModelValue('days')
    const startDate = form.value?.getModelValue('startDate')
    if (licenseType !== '' && days && days !== '' && startDate !== '') {
      try {
        const _getEndDate = await getEndDate({
          licenseTypeId: licenseType,
          days,
          startDate
        })
        form.value?.setModelValue('endDate', _getEndDate)
      } catch (error) {
        notificationError(error, 'licenses', t, notifications, loading)
      }
    }
  } else if (field.name === 'employeeId') {
    const employeeId = form.value?.getModelValue('employeeId')
    if (field.name === 'employeeId') {
      const journeyId = await getJourneyEmployee(employeeId)
      form.value?.setModelValue('journeyId', journeyId)
    }
  } else if (field.name === 'custom:mxSequel') {
    // Reglas para mostrar custom:mxPercentage
    const sequelValue = form.value?.getModelValue('custom:mxSequel')
    var showMxPercentageField = [
      "706926b3-8780-4ed0-aed8-667d0fd6309c",
      "34a96b96-73f7-482b-854b-f5140e515172",
      "0de7a326-b90d-41d9-880c-db773f024150",
      "df712cda-5ed9-4145-8872-b2e8658f2e18",
      "781f14dc-e81b-4f1a-8fe7-b076f67851ce"
    ].includes(sequelValue)
    form.value?.setModelValue('custom:showPercentage', showMxPercentageField)
  }
}

const getEntity = async () => {
  if (!route.params.id) return
  model = await getLicenseRequest(route.params.id as string)
  if (model === undefined) {
    notifications.addNotification({
      title: t('notification.notFound'),
      text: t('licenses.notification.error'),
      type: 'error',
      time: 10000
    })
    back()
  }
}

/* Volver */
const back = () => {
  void router.push({ name: 'ListLicenseRequests' })
}

let validationPercentage = false
const validationSchema = object().shape({

  paymentReference: mixed().when('banks', (tipo: any, schema) => {
    if ( validationPercentage ) {
      return string().required(t('form.valid.required'))
    } else {
      return string()
    }
    return schema
  })
})

/* Uso del metodo de validación, retorno de errores y metodo para reiniciar formulario */
const { handleSubmit, errors } = useForm<any /*paramentPayrollStandard*/>({
  initialValues: {},
  validationSchema
})

onBeforeMount(async () => {
  loading.value = true
  try {
    await getEntity()
    customForm.value = await getForm({
      entity: 'license-request',
      country: model.isoCode,
      company: model.companyId
    })
    if (route.params.id !== undefined) messageLoading.value = t('loading.data')
    loading.value = false
  } catch (error: any) {
    if (error.response.status === 400) {
      notifications.addNotification({
        title: t('notification.notFound'),
        text: t('licenses.notification.error'),
        type: 'error',
        time: 10000
      })
      back()
    } else {
      notificationError(error, 'licenses', t, notifications, loading)
    }
  }
})
</script>
<template>
  <DLoading v-show="loading" :message="messageLoading" />
  <div class="h-full px-5">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow text-blue-900"
          v-html="
            route.params.id == undefined
              ? t('licenses.create')
              : t('licenses.edit')
          " />
      </div>
    </div>
    <DynamicForm
      ref="form"
      v-if="customForm?.tabs"
      :form-schema="customForm"
      :initial-model="model"
      :title-btn-save="
        route.params.id === undefined
          ? t('form.button.save')
          : t('form.button.update')
      "
      @cancel="back"
      @update-field="onUpdateField"
      @submit="onSubmit" />
  </div>
</template>
